import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuCalendarPlus, LuUsers, LuReceipt, LuClock, LuCheck, LuUserCheck } from "react-icons/lu";
import { capitalize } from "lodash";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import StatCard from "../ui/stat-card";
import BarChart from "../ui/bar-chart";
import useFetchStatistics from "./hooks/useFetchStatistics";

const ClubManagementStatistics = (): ReactElement => {
  const { t } = useTranslation();
  const currentDate = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());

  const { statistics, loading } = useFetchStatistics(selectedYear);

  const years = useMemo(() => {
    const currentYear = currentDate.getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, [currentDate]);

  // Transform API data to component format
  const monthlyHoursData = useMemo(() => {
    if (!statistics) {
      return [];
    }

    return statistics.monthly_hours.map(data => {
      const monthDate = new Date(selectedYear, data.month - 1, 1);
      const monthName = capitalize(format(monthDate, "MMMM", { locale: it }));

      return {
        month: monthName,
        members: data.members,
        nonMembers: data.non_members,
      };
    });
  }, [statistics, selectedYear]);

  // Format data for Recharts (use abbreviated month names for X-axis)
  const chartData = useMemo(
    () =>
      monthlyHoursData.map(data => ({
        month: data.month.substring(0, 3), // First 3 letters of month
        fullMonth: data.month,
        members: data.members,
        nonMembers: data.nonMembers,
      })),
    [monthlyHoursData],
  );

  // Bar chart configuration
  const barChartBars = useMemo(
    () => [
      {
        dataKey: "members",
        fill: "#3182CE",
        name: t("club_management.statistics.chart.legend.members"),
      },
      {
        dataKey: "nonMembers",
        fill: "#A0AEC0",
        name: t("club_management.statistics.chart.legend.non_members"),
      },
    ],
    [t],
  );

  if (loading || !statistics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="200px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header with Year Selector */}
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        gap={3}
        width="100%"
      >
        <Heading size="lg">{t("club_management.statistics.title")}</Heading>
        <FormControl w={{ base: "100%", md: "150px" }}>
          <FormLabel>{t("club_management.statistics.filters.year")}</FormLabel>
          <Select
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            size="sm"
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        <StatCard
          icon={LuCalendarPlus}
          value={statistics.total_bookings}
          label={t("club_management.statistics.cards.bookings")}
          colorScheme="blue"
        />
        <StatCard
          icon={LuUsers}
          value={statistics.total_users}
          label={t("club_management.statistics.cards.registered")}
          colorScheme="cyan"
        />
        <StatCard
          icon={LuReceipt}
          value={statistics.total_receipts}
          label={t("club_management.statistics.cards.receipts")}
          colorScheme="orange"
        />
        <StatCard
          icon={LuClock}
          value={statistics.unpaid_hours}
          label={t("club_management.statistics.cards.unpaid_hours")}
          colorScheme="red"
        />
        <StatCard
          icon={LuCheck}
          value={statistics.paid_hours}
          label={t("club_management.statistics.cards.paid_hours")}
          colorScheme="green"
        />
        <StatCard
          icon={LuUserCheck}
          value={statistics.paid_hours_members}
          label={t("club_management.statistics.cards.paid_hours_members")}
          colorScheme="green"
        />
      </SimpleGrid>

      {/* Table and Chart */}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        {/* Monthly Hours Table */}
        <GridItem>
          <Box>
            <Heading size="md" mb={4}>
              {t("club_management.statistics.monthly_hours.title")}
            </Heading>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>{t("club_management.statistics.monthly_hours.columns.month")}</Th>
                    <Th isNumeric>
                      {t("club_management.statistics.monthly_hours.columns.members")}
                    </Th>
                    <Th isNumeric>
                      {t("club_management.statistics.monthly_hours.columns.non_members")}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {monthlyHoursData.map(data => (
                    <Tr key={data.month}>
                      <Td>{data.month}</Td>
                      <Td isNumeric>{data.members}</Td>
                      <Td isNumeric>{data.nonMembers}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </GridItem>

        {/* Bar Chart */}
        <GridItem>
          <Box>
            <Heading size="md" mb={4}>
              {t("club_management.statistics.chart.title")}
            </Heading>
            <Box p={4} bg="gray.50" borderRadius="md">
              <BarChart data={chartData} bars={barChartBars} />
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </VStack>
  );
};

export default ClubManagementStatistics;
